import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router, RouterModule } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { DrawerModule } from 'primeng/drawer';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IconComponent, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { environment } from '../../../../environments/environment.dev';
import { ButtonComponent } from "../../../@core/components/ui/button/button.component";
import { TRIAL_LIFECYCLE_PHASE, SUBSCRIPTION_STATUS, USER_ROLE } from '../../../@core/models/enums';
import { CaptureResult, IUser, PAYMENT_STATUS } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { FeatureFlagService } from '../../../@core/services/feature-flag.service';
import { ProductWalkThroughService } from '../../../@core/services/product-walk-through.service';
import { SubscriptionStateService } from '../../../@core/services/subscription-state.service';
import { BreadcrumbComponent } from '../../../@shared/components/breadcrumb/breadcrumb.component';
import { CaptureIssueComponent } from '../../../@shared/components/capture-issue/capture-issue.component';
import { NotificationsComponent } from "../../../@shared/components/notifications/notifications.component";
import { SupportTicketFormComponent } from '../../../@shared/components/support-ticket-form/support-ticket-form.component';
import { ModifierKey } from '../../../@shared/models/keyboard-shortcut.models';
import { TextCasePipe } from '../../../@shared/pipes/textCase.pipe';
import { KeyboardShortcutService } from '../../../@shared/services/keyboard-shortcut.service';
import { ScreenRecordingService } from '../../../@shared/services/screen-recording.service';
import { SupportTicketDialogService } from '../../../@shared/services/support-ticket-dialog.service';
import { SVG, TOP_BAR_SVG } from '../../../common/svgs';
import { PRODUCT_WALKTHROUGH_TYPE } from '../../../contract-management/models/contract-management.model';
import { SidebarService } from '../../services/sidebar.service';
import { CreditsOverviewComponent } from '../../../features/credits/components/credits-overview/credits-overview.component';



@Component({
  selector: 'app-top-bar',
  standalone: true,
  imports: [AvatarModule, MenuComponent, CommonModule, IconComponent, BreadcrumbComponent, AsyncPipe, DrawerModule, ButtonModule, BadgeModule, RouterModule, NotificationsComponent, DialogComponent, SupportTicketFormComponent, TooltipModule, ToastModule, CaptureIssueComponent, ButtonComponent, TextCasePipe, CreditsOverviewComponent],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss'
})
export class TopBarComponent implements OnInit, OnDestroy {
  @Input() sidebarId: string = ''; // Default sidebar ID
  @ViewChild('supportTicketForm') supportTicketFormComponent!: SupportTicketFormComponent;
  @ViewChild('captureIssue') captureIssueComponent!: CaptureIssueComponent;

  private breakpointObserver = inject(BreakpointObserver);
  private breakpointSubscription?: Subscription;

  isMobile = signal<boolean>(false);
  isGeneralAgentRoute = signal<boolean>(false);

  items: MenuItem[] | undefined;
  sidebarVisible: boolean = false;
  currentUser!: IUser | null | undefined;
  presentWalkthroughScreen: PRODUCT_WALKTHROUGH_TYPE | null = null
  ENV = environment
  SVG = SVG;
  TOP_BAR_SVG = TOP_BAR_SVG;

  isExpanded: boolean = false;
  supportTicketDialogVisible: boolean = false;
  sidebarVisible$!: Observable<boolean>;
  isRecording: boolean = false;

  supportMenuItems: CustomMenuItem[] = [
    {
      icon: SVG.RAISE_A_TICKET,
      iconSource: IconSourceEnum.SVG,
      label: 'Raise a support ticket',
      callback: () => {
        this.openHelp();
      }
    },
    {
      icon: SVG.CAPTURE_AN_ISSUE,
      iconSource: IconSourceEnum.SVG,
      label: 'Capture an issue',
      callback: () => {
        this.openCapture();
      }
    }
  ];

  baseInfoMenuItems: CustomMenuItem[] = [
    {
      icon: TOP_BAR_SVG.DOCUMENTATION,
      iconSource: IconSourceEnum.SVG,
      label: 'Documentation',
      callback: () => {
        window.open('http://help.doc.inncircles.ai/', '_blank');
      }
    },
    {
      icon: TOP_BAR_SVG.WEBINARS,
      iconSource: IconSourceEnum.SVG,
      label: 'Webinars',
      callback: () => {
        window.open('https://inncircles.com/join-webinar/', '_blank');
      }
    },
    {
      icon: TOP_BAR_SVG.WHATS_NEW,
      iconSource: IconSourceEnum.SVG,
      label: "What's New",
      callback: () => {
        window.open('https://inncircles.ai/blogs/', '_blank');
      }
    },

  ];
  infoMenuItems: CustomMenuItem[] = this.baseInfoMenuItems;


  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private productWalkThroughService: ProductWalkThroughService,
    private sidebarService: SidebarService,
    private keyboardShortcutService: KeyboardShortcutService,
    private supportTicketDialogService: SupportTicketDialogService,
    private screenRecordingService: ScreenRecordingService,
    private features: FeatureFlagService,
    public subscriptionState: SubscriptionStateService
  ) {
    this.authService.currentUser.subscribe(res => this.currentUser = res)
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.updatePresentWalkthrough(event.urlAfterRedirects);
          this.checkGeneralAgentRoute(event.urlAfterRedirects);
        }
      });
  }

  ngOnInit(): void {
    this.screenRecordingService.recordingState$.subscribe(state => {
      this.isRecording = state.isRecording;
    });
    this.updatePresentWalkthrough(this.router.url);
    this.updateMenuItems(this.subscriptionState.hasSubscriptionEnded());

    // Subscribe to sidebar visibility for the specific sidebar
    this.sidebarVisible$ = this.sidebarService.getSidebarVisible$(this.sidebarId);

    // Subscribe to support ticket dialog open requests
    this.supportTicketDialogService.openDialog$.subscribe(payload => {
      if (payload.attachment && this.supportTicketFormComponent) {
        this.supportTicketFormComponent.attachmentFiles.push(payload.attachment.file);
        this.supportTicketFormComponent.attachmentPreviews.push(payload.attachment);
      }
      this.supportTicketDialogVisible = true;
    });
    this.updatePresentWalkthrough(this.router.url);
    this.checkGeneralAgentRoute(this.router.url);

    // Initialize mobile detection
    this.isMobile.set(this.breakpointObserver.isMatched('(max-width: 767px)'));
    this.breakpointSubscription = this.breakpointObserver
      .observe('(max-width: 767px)')
      .subscribe(result => this.isMobile.set(result.matches));
  }

  checkGeneralAgentRoute(url: string): void {
    this.isGeneralAgentRoute.set(url.includes('general-agent'));
  }

  toggleIcon() {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Get the keyboard shortcut text for the sidebar toggle
   * Shows Cmd+S on Mac, Ctrl+S on Windows/Linux
   */
  get sidebarToggleShortcut(): string {
    // Use the platform-specific formatting from the keyboard shortcut service
    // On Mac, CMD key is used; on Windows/Linux, CTRL is used
    const isMac = this.keyboardShortcutService.isMacPlatform();
    return this.keyboardShortcutService.formatShortcutForDisplay('s', isMac ? ModifierKey.CMD : ModifierKey.CTRL);
  }

  get subscriptionPhaseLabel(): { label: string, severity: string, styleClass: string } {
    if (this.subscriptionState.trialLifecyclePhase()) {
      switch (this.subscriptionState.trialLifecyclePhase()) {
        case TRIAL_LIFECYCLE_PHASE.ACTIVE_TRIAL:
          return { label: `Trial ends in ${this.subscriptionState.trialDaysLeftFormatted()}`, severity: "theme", styleClass: 'bg-blue-100 text-blue-800' };
        case TRIAL_LIFECYCLE_PHASE.EXPIRED_GRACE:
          return { label: 'Trial ended - Get plan', severity: "contrast", styleClass: 'border border-red-600 text-red-600' };
        case TRIAL_LIFECYCLE_PHASE.INACTIVE_PENDING_DELETION:
          return { label: 'Account inactive - Get plan', severity: "danger", styleClass: 'border border-red-100 text-red-800' };
        case TRIAL_LIFECYCLE_PHASE.DELETED:
          return { label: 'Account deleted', severity: "secondary", styleClass: 'border border-gray-100 text-gray-800' };
      }
    }

    return { label: this.subscriptionState.subscriptionDetails()?.plan?.name || '', severity: "theme", styleClass: 'bg-green-100 text-green-800' };
  }

  get subscriptionPaymentStatusLabel(): { label: string, severity: string, styleClass: string } {
    const subscription = this.subscriptionState.subscriptions();
    const plan = this.subscriptionState.plan();
    const planName = typeof plan === 'string' ? plan : plan?.name || '';

    if (!subscription || subscription.status !== SUBSCRIPTION_STATUS.ACTIVE) {
      return { label: planName || 'No active subscription', severity: 'danger', styleClass: 'border border-red-600 text-red-600' };
    }

    switch (subscription.paymentStatus) {
      case PAYMENT_STATUS.PAID:
        return {
          label: planName || 'Active subscription',
          severity: 'theme',
          styleClass: 'bg-green-100 text-green-800'
        };
      case PAYMENT_STATUS.PENDING:
        return {
          label: 'Renew subscription',
          severity: 'danger',
          styleClass: 'border border-red-600 text-red-600'
        };
      case PAYMENT_STATUS.FAILED:
      case PAYMENT_STATUS.UNPAID:
        return {
          label: 'Subscription suspended - Renew now',
          severity: 'danger',
          styleClass: 'border border-red-600 text-red-600'
        };
      default:
        return {
          label: 'Subscription status unavailable',
          severity: 'danger',
          styleClass: 'border border-red-600 text-red-600'
        };
    }
  }

  canAccessPricing(): boolean {
    return this.currentUser?.role === USER_ROLE.SUPER_ADMIN;
  }

  /**
   * Update menu items based on subscription status
   * Locks Profile and Settings when subscription has ended
   */
  private updateMenuItems(hasSubscriptionEnded: boolean): void {
    const menuItems: MenuItem[] = [
      {
        items: [
          {
            label: 'Profile',
            icon: 'pi pi-user',
            route: hasSubscriptionEnded ? '' : '/profile', // Lock route if subscription ended
            disabled: hasSubscriptionEnded, // Disable menu item
            styleClass: hasSubscriptionEnded ? 'opacity-50 cursor-not-allowed' : ''
          },
          {
            label: 'Settings',
            icon: 'pi pi-cog',
            route: hasSubscriptionEnded ? '' : '/settings', // Lock route if subscription ended
            disabled: hasSubscriptionEnded, // Disable menu item
            styleClass: hasSubscriptionEnded ? 'opacity-50 cursor-not-allowed' : '',
            visible: this.canSeeSettings // Only show if user has permission
          },
          {
            label: 'Log Out',
            icon: 'pi pi-sign-in',
            route: '',
          }
        ]
      }
    ];

    this.items = menuItems;
  }

  get canSeeSettings(): boolean {
    if (!this.currentUser) return false;
    const hasAdminRole = this.currentUser.role === USER_ROLE.ADMIN || this.currentUser.role === USER_ROLE.SUPER_ADMIN;
    const hasWorkspaceRole = this.currentUser.workspaceRole && ['OWNER', 'ADMIN', 'VIEW', 'COMMENT'].includes(this.currentUser.workspaceRole);
    return hasAdminRole || !!hasWorkspaceRole;
  }

  handleClick(item: { label?: string, icon?: string, route?: string, extraRouteArgs?: NavigationExtras }) {
    if (item.label === 'Log Out') {
      this.authService.logout();
    } else if (item.label == 'Profile' || item.label === 'Settings') {
      // Only navigate if route is not empty (not locked)
      if (item.route) {
        this.router.navigate([item.route || '']);
      }
    }
  }

  updatePresentWalkthrough(url: string) {
    // Extract pathname to handle query parameters
    const pathname = url.split('?')[0];
    if (url.includes('settings'))
      this.presentWalkthroughScreen = PRODUCT_WALKTHROUGH_TYPE.SETTINGS
    else if (pathname === '' || pathname === '/' || pathname === '/explore')
      this.presentWalkthroughScreen = PRODUCT_WALKTHROUGH_TYPE.HOME
    else if (url.includes('compare'))
      this.presentWalkthroughScreen = PRODUCT_WALKTHROUGH_TYPE.COMPARE
    else if (url.includes('questionnaire'))
      this.presentWalkthroughScreen = PRODUCT_WALKTHROUGH_TYPE.QUESSTIONAIRE
    else if (url.includes('/projects/dashboard'))
      this.presentWalkthroughScreen = PRODUCT_WALKTHROUGH_TYPE.PROJECT_DASHBOARD
    else
      this.presentWalkthroughScreen = null


    if (this.presentWalkthroughScreen) {
      this.infoMenuItems = [...this.baseInfoMenuItems, {
        icon: TOP_BAR_SVG.QUICK_TOUR,
        iconSource: IconSourceEnum.SVG,
        label: 'Quick Tour',
        callback: () => {
          this.resetWalkthrough();
        }
      }];
    } else {
      this.infoMenuItems = [...this.baseInfoMenuItems];
    }
  }

  resetWalkthrough() {
    if (this.presentWalkthroughScreen)
      this.productWalkThroughService.resetWalkThrough(this.presentWalkthroughScreen);
  }

  openHelp() {
    this.supportTicketDialogVisible = true;
  }

  openCapture() {
    this.captureIssueComponent.openCaptureDialog();
  }

  onCaptureCompleted(result: CaptureResult) {
    this.supportTicketDialogVisible = true;
    setTimeout(() => {
      if (this.supportTicketFormComponent) {
        this.supportTicketFormComponent.attachmentFiles.push(result.file);
        this.supportTicketFormComponent.attachmentPreviews.push(result);
      }
    }, 0);
  }

  onSupportTicketSubmitted() {
    this.supportTicketDialogVisible = false;
  }

  onSupportTicketDialogHide() {
    if (this.supportTicketFormComponent) {
      this.supportTicketFormComponent.resetForm();
    }
    this.supportTicketDialogVisible = false;
  }

  navigateToPricing() {
    this.router.navigate(['/pricing']);
  }

  ngOnDestroy() {
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
  }

  toggleSidebar(): void {
    this.sidebarService.toggleSidebar(this.sidebarId);
  }
}

